import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import { invoiceQueryKeys } from "../api/queryKeys";

import { instance } from "@/lib/axios/instance";
import {
  handleProblemDetailsError,
  ProblemDetails,
} from "@/lib/helpers/handleProblemDetailsError";
import useToast from "@/lib/hooks/useToast";

export type CampaignIntegrationInvoiceItem = {
  campaignId: string;
  clientIntegrationModuleId: string;
  clientId2: string;
  amount: number;
};

export type BulkCreateCampaignIntegrationInvoicesRequest = {
  invoices: CampaignIntegrationInvoiceItem[];
};

type UseBulkCreateCampaignIntegrationInvoicesParams = {
  tenantIntegrationModuleId: string;
  year: string;
  month: string;
  tenantId?: string | null;
};

/**
 * Creates campaign integration invoices in bulk for a tenant integration module and month.
 * Calls POST /api/v2/Integrations/{tenantIntegrationModuleId}/Invoices/{year}/{month}/Bulk.
 */
export function useBulkCreateCampaignIntegrationInvoices(
  params: UseBulkCreateCampaignIntegrationInvoicesParams
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    AxiosResponse,
    AxiosError<ProblemDetails>,
    BulkCreateCampaignIntegrationInvoicesRequest
  >({
    mutationFn: (request: BulkCreateCampaignIntegrationInvoicesRequest) =>
      instance
        .post<AxiosResponse>(
          `/api/v2/Integrations/${params.tenantIntegrationModuleId}/Invoices/${params.year}/${params.month}/Bulk`,
          { invoices: request.invoices },
          {
            params: {
              tenantId: params.tenantId || undefined,
            },
          }
        )
        .then((res) => res.data),
    onSuccess: () => {
      // Refetch the local page's data (no optimistic update on create, need server data)
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.integrationModuleCampaigns.byIntegrationModuleYearMonth(
          params.tenantIntegrationModuleId,
          params.year,
          params.month
        ),
      });
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.integrationModuleInvoices.detail(
          params.tenantIntegrationModuleId,
          params.year,
          params.month
        ),
      });
      // Cross-page queries: mark stale, refetchOnMount: true handles refresh on navigation
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.tenantJobBoards.byTenant(params.tenantId ?? null) });
      queryClient.invalidateQueries({ queryKey: ["campaignJobBoards"] });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.campaignJobBoardMonths.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clientBudgetMonth.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clients.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.transactions.all() });
      showToast(t("success.save", { entity: t("common.invoices") }));
    },
    onError: (error) => {
      handleProblemDetailsError({
        error,
        t,
        showToast,
        fallbackMessage: t("errors.somethingWentWrong"),
      });
    },
  });
}
